<template>
  <div class="crossbeam">
    <div v-if="initializationFailed" data-testid="application-failed">
      The application failed to load. Please try again or contact
      <a href="mailto:support@crossbeam.com">support@crossbeam.com</a>.
    </div>
    <router-view v-else v-slot="{ Component }">
      <transition name="fade">
        <div v-if="Component" id="full-app">
          <TalkToSalesModal
            v-if="talkToSalesModalVisible"
            @action="talkToSalesModalVisible = false"
            @close-modal="talkToSalesModalVisible = false"
          />
          <BillingPlanComparisonModal
            v-if="comparisonPlanModalVisible"
            :billing-interaction
            :billing-query
            @close-modal="comparisonPlanModalVisible = false"
          />
          <AIChat
            v-if="hasAccessToAiChat"
            :visible="aiChatVisible"
            @ai-chat-closed="() => (aiChatVisible = false)"
          />
          <component :is="Component" />
        </div>
      </transition>
    </router-view>
  </div>
</template>

<script setup>
import { EVENT_SITES } from '@crossbeam/itly';

import { useHead } from '@unhead/vue';
import { storeToRefs } from 'pinia';
import { computed, onMounted, provide, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';

import TalkToSalesModal from '@/components/billing/TalkToSalesModal.vue';
import AIChat from '@/components/navbarDeux/AIChat.vue';

import { useAIChatState } from '@/composables/mcp/useAIChatState';
import useAuth from '@/composables/useAuth';
import useIteratively from '@/composables/useIteratively';
import useMinimalApp from '@/composables/useMinimalApp';
import { FEATURE_FLAG } from '@/constants/feature_flags';
import { captureException } from '@/errors';
import {
  ProvidedOpenComparisonPlanModal,
  ProvidedOpenTalkToSalesModal,
} from '@/injectionKeys/billing';
import {
  ProvidedAIChatState,
  ProvidedOpenAiChat,
} from '@/injectionKeys/global';
import { useFeatureFlagStore } from '@/stores';
import { useWebSocketStore } from '@/stores/MCPWebsocketStore';
import { useRootStore } from '@/stores/RootStore';

import BillingPlanComparisonModal from './billing/BillingPlanComparisonModal.vue';

const route = useRoute();
const router = useRouter();
const { initializationFailed, isCrossbeamUser, inMaintenanceMode } = useAuth();
const ffStore = useFeatureFlagStore();

const { hasSalesOnlyRole } = storeToRefs(useRootStore());

const { iteratively } = useIteratively();

const openIntercom = computed(() => route.query.intercom === 'open');

const hasAccessToAiChat = computed(() => {
  if (hasSalesOnlyRole.value) return false;
  return hasAIChat.value || hasAdminOnlyAIChat.value;
});

const aiChatVisible = ref(false);
provide(ProvidedOpenAiChat, () => {
  if (hasAccessToAiChat.value) {
    const wsStore = useWebSocketStore();
    aiChatVisible.value = true;
    iteratively.aiChatAction({
      event_site: EVENT_SITES.AI_CHAT,
      type: 'chat_opened',
      conversation_id: wsStore?.chatSessionId || '',
    });
  }
});

const talkToSalesModalVisible = ref(false);
const billingInteraction = ref(null);
const billingQuery = ref(null);
provide(ProvidedOpenTalkToSalesModal, () => {
  talkToSalesModalVisible.value = true;
});

const comparisonPlanModalVisible = ref(false);
provide(ProvidedOpenComparisonPlanModal, (opts, route) => {
  billingInteraction.value = opts;
  billingQuery.value = route;
  comparisonPlanModalVisible.value = true;
});
/**
 * We are watching for maintenance mode state coming from statuspage. If we have a
 * valid event, we punt user to the maintenance mode page.
 *
 * We punt the user back home if the old maintenance event is in state and has been reset to null
 * because no in_progress events are coming through. This essentially means maintenance mode has
 * been marked as completed
 */
watch(inMaintenanceMode, (newVal, oldVal) => {
  if (newVal?.id) router.push({ name: 'maintenance_mode' });
  else if (oldVal?.id && newVal === null) router.push({ name: 'home' });
});

useHead({
  title: 'Crossbeam',
});

const { isMinimalView } = useMinimalApp();
const hasAIChat = computed(() =>
  ffStore.hasFeatureFlag(FEATURE_FLAG.TEMP_AI_CHAT),
);
const hasAdminOnlyAIChat = computed(() => {
  if (!ffStore.hasFeatureFlag(FEATURE_FLAG.TEMP_ADMIN_AI_CHAT)) return false;
  return isCrossbeamUser.value;
});
onMounted(() => {
  if (openIntercom.value && !isMinimalView) {
    this.$nextTick(() => {
      try {
        window.Intercom('show');
      } catch (err) {
        captureException(err);
      }
    });
  }
  if (hasAccessToAiChat.value) {
    provide(ProvidedAIChatState, useAIChatState());
  }
});
</script>

<style lang="pcss">
#app {
  display: flex;
  flex-direction: column;
}
.crossbeam {
  position: relative;
  min-height: 100vh;
  height: 100vh;
}
@media (max-width: 768px) {
  .intercom-lightweight-app {
    display: none;
  }
}
</style>
